import { Box, Grid, List, ListItem, ListItemText } from '@material-ui/core'
import { t } from 'i18next'
import React, { useEffect, useState } from 'react'
import { i18n } from '../../../i18n'
import {
  CalibrationChartType,
  COLOR_SCHEME_SET1,
  CustomSeries,
} from '../calibration/chart-util'
import { formatHour, FullFormatOptions } from '../../../util/formatters'
import {
  BuildingEnergyModelFragment,
  HourSeriesType,
} from '../../../generated/graphql'
import {
  calcSummaryHourlyCoolingChartData,
  calcSummaryHourlyHeatingChartData,
  calcSummaryMonthlyElectricityChartData,
  calcSummaryMonthlyHeatingChartData,
} from './summary-util'
import { HourSeriesResult } from '../../../building-energy-model/hour-series-calculator'
import { LoadingDialog } from '../../common/LoadingDialog'
import { SummaryLineGraphContent } from './SummaryLineGraphContent'
import { SummaryBarGraphContent } from './SummaryBarGraphContent'
import { EmptyChartContainer } from './EmptyChartContainer'

const xValueHourFormatter = (value: number) => formatHour(value)
const yValueFixedNumberFormatter = (value: number) => value.toFixed(2)

type SummaryGraphData = {
  id: number
  name: string
  measuredData: CustomSeries
  energySystemData: CustomSeries[]
  dependentHourSerieType: HourSeriesType
  chartType: CalibrationChartType
  xFormatter: (value: number) => string
  yFormatter: (value: number, options?: FullFormatOptions) => string
  axisBottomLegend?: string
  axisLeftLegend?: string
  showPoints?: boolean
  legendItemWidth?: number
  grouped?: boolean
}

type Props = {
  model: BuildingEnergyModelFragment
  year: number
  hourSeries: HourSeriesResult[]
  updatedHourSeries: HourSeriesType[]
}

export function BemSummaryCharts({
  model,
  year,
  hourSeries,
  updatedHourSeries,
}: Props): React.ReactElement {
  const [chartData, setChartData] = useState<SummaryGraphData>()
  const [selectedChart, setSelectedChart] = useState(0)
  const [loading, setLoading] = useState(true)

  const chartNumberChanged = (index: number) => {
    setSelectedChart(index)
  }

  function updateEnergySystemData(chartIndex: number) {
    switch (chartIndex) {
      case 0:
        setChartData({
          id: 0,
          name: i18n.t(
            'buildingEnergyModels.summary.energyModelCharts.montlyHeatingConsumtion',
          ),
          measuredData: { id: '', data: [] },
          energySystemData: calcSummaryMonthlyHeatingChartData(
            hourSeries.filter(
              (s) => s.hourSerieType === HourSeriesType.HeatingDemand,
            ),
            year,
          ),
          dependentHourSerieType: HourSeriesType.HeatingDemand,
          chartType: CalibrationChartType.Bar,
          xFormatter: xValueHourFormatter,
          yFormatter: yValueFixedNumberFormatter,
        })

        break
      case 1:
        setChartData({
          id: 1,
          name: i18n.t(
            'buildingEnergyModels.summary.energyModelCharts.hourlyHeatingPowerDemand',
          ),
          measuredData: { id: '', data: [] },
          energySystemData: calcSummaryHourlyHeatingChartData(
            hourSeries.filter(
              (s) => s.hourSerieType === HourSeriesType.HeatingDemand,
            ),
          ),
          dependentHourSerieType: HourSeriesType.HeatingDemand,
          chartType: CalibrationChartType.Line,
          xFormatter: xValueHourFormatter,
          yFormatter: yValueFixedNumberFormatter,
        })
        break
      case 2:
        setChartData({
          id: 2,
          name: i18n.t(
            'buildingEnergyModels.summary.energyModelCharts.monthlyElectricityConsumption',
          ),
          measuredData: { id: '', data: [] },
          energySystemData: calcSummaryMonthlyElectricityChartData(
            hourSeries.filter(
              (s) => s.hourSerieType === HourSeriesType.ElectricityDemand,
            ),
            year,
          ),
          dependentHourSerieType: HourSeriesType.ElectricityDemand,
          chartType: CalibrationChartType.Bar,
          xFormatter: xValueHourFormatter,
          yFormatter: yValueFixedNumberFormatter,
        })
        break
      case 3:
        setChartData({
          id: 3,
          name: i18n.t(
            'buildingEnergyModels.summary.energyModelCharts.hourlyCoolingPowerDemand',
          ),
          measuredData: { id: '', data: [] },
          energySystemData: calcSummaryHourlyCoolingChartData(
            hourSeries.filter(
              (s) => s.hourSerieType === HourSeriesType.CoolingDemand,
            ),
          ),
          dependentHourSerieType: HourSeriesType.CoolingDemand,
          chartType: CalibrationChartType.Line,
          xFormatter: xValueHourFormatter,
          yFormatter: yValueFixedNumberFormatter,
        })
        break
      default:
        break
    }
  }

  const updateChartData = (chartIndex: number) => {
    updateEnergySystemData(chartIndex)
  }

  useEffect(() => {
    if (hourSeries.length > 0) updateChartData(selectedChart)
    setLoading(false)
  }, [selectedChart, model, hourSeries]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      hourSeries.length > 0 &&
      updatedHourSeries.find((h) => h === chartData?.dependentHourSerieType)
    )
      updateChartData(selectedChart)

    setLoading(false)
  }, [updatedHourSeries]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <Grid container direction="row" alignItems="center">
        <Grid item xs={3}>
          <List component="nav">
            <ListItem
              button
              selected={selectedChart === 0}
              onClick={() => chartNumberChanged(0)}
            >
              <ListItemText
                primary={`${t(
                  'buildingEnergyModels.summary.energyModelCharts.montlyHeatingConsumtion',
                )}`}
                primaryTypographyProps={{ variant: 'subtitle1' }}
              />
            </ListItem>
            <ListItem
              button
              selected={selectedChart === 1}
              onClick={() => chartNumberChanged(1)}
            >
              <ListItemText
                primary={`${t(
                  'buildingEnergyModels.summary.energyModelCharts.hourlyHeatingPowerDemand',
                )}`}
                primaryTypographyProps={{ variant: 'subtitle1' }}
              />
            </ListItem>
            <ListItem
              button
              selected={selectedChart === 2}
              onClick={() => chartNumberChanged(2)}
            >
              <ListItemText
                primary={`${t(
                  'buildingEnergyModels.summary.energyModelCharts.monthlyElectricityConsumption',
                )}`}
                primaryTypographyProps={{ variant: 'subtitle1' }}
              />
            </ListItem>
            <ListItem
              button
              selected={selectedChart === 3}
              onClick={() => chartNumberChanged(3)}
            >
              <ListItemText
                primary={`${t(
                  'buildingEnergyModels.summary.energyModelCharts.hourlyCoolingPowerDemand',
                )}`}
                primaryTypographyProps={{ variant: 'subtitle1' }}
              />
            </ListItem>
          </List>
        </Grid>
        <Grid item xs={9}>
          <Box border={1} borderColor="lightgray" paddingTop={3}>
            {chartData &&
              chartData.chartType === CalibrationChartType.Line &&
              chartData.energySystemData.length > 0 && (
                <SummaryLineGraphContent
                  energySystemData={chartData.energySystemData}
                  xFormatter={chartData.xFormatter}
                  yFormatter={chartData.yFormatter}
                  height={540}
                  axisBottomLegend={chartData.axisBottomLegend}
                  axisLeftLegend={chartData.axisLeftLegend}
                  showPoints={chartData.showPoints}
                  legendItemWidth={chartData.legendItemWidth}
                />
              )}
            {chartData &&
              chartData.chartType === CalibrationChartType.Bar &&
              chartData.energySystemData.length > 0 && (
                <SummaryBarGraphContent
                  energySystemData={chartData.energySystemData}
                  height={540}
                  axisBottomLegend={chartData.axisBottomLegend}
                  axisLeftLegend={chartData.axisLeftLegend}
                  legendItemWidth={chartData.legendItemWidth}
                  grouped={chartData.grouped}
                  colors={COLOR_SCHEME_SET1}
                />
              )}
            {(!chartData || chartData.energySystemData.length === 0) && (
              <EmptyChartContainer
                text={t(
                  'buildingEnergyModels.summary.energyModelCharts.noChartData',
                )}
                height={540}
              />
            )}

            <LoadingDialog isLoading={loading} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}
